import moment from 'moment';

import settings from '~/settings';
import { sawClanStars, sawPatrols } from '~/constants';
import { getValueFromLocalStorage } from '~/helpers/localStorage';
import { isChinaRealm } from '~/helpers/realm';
import { hasPermission, PERMISSIONS } from '~/roles';
import { ROUTES_BADGES, ROUTES_BADGES_TYPES } from '~/router/routes';

import type { BadgeModel } from './types';
import type { NavigationContext } from '~/store/selectors/navigationSelectors';

const newLabel = () => (isChinaRealm() ? '新' : 'NEW');

const toCountText = (value: number) => {
  if (value > 99) {
    return '99+';
  }
  return value;
};

export const getBadgeModel = (
  badge: ROUTES_BADGES | undefined,
  {
    currentAccount,
    clanStarsNewRewards,
    clanTasksUnclaimedAmount,
    canClaimRewards,
    isClanHubEnabled,
  }: NavigationContext,
): BadgeModel => {
  if (!badge) {
    return { kind: ROUTES_BADGES_TYPES.NONE };
  }

  // REQUESTS: numeric badge
  if (badge === ROUTES_BADGES.REQUESTS) {
    const isRecruiter = !!currentAccount.roleName && hasPermission(currentAccount.roleName, PERMISSIONS.HANDLE_INVITES);
    const value =
      currentAccount.clanId && isRecruiter ? currentAccount.activeApplicationsCount : currentAccount.activeInvitesCount;

    if (value) {
      return {
        kind: ROUTES_BADGES_TYPES.COUNTER,
        value: toCountText(value),
        id: ROUTES_BADGES.REQUESTS,
      };
    }

    return {
      kind: ROUTES_BADGES_TYPES.NONE,
    };
  }

  // CLAN_STARS: numeric badge from selector
  if (badge === ROUTES_BADGES.CLAN_STARS && clanStarsNewRewards) {
    // Special case: if clanHub is enabled, show reward badge instead of counter
    if (isClanHubEnabled) {
      return {
        kind: ROUTES_BADGES_TYPES.REWARD,
        id: ROUTES_BADGES.CLAN_STARS,
      };
    }

    return {
      kind: ROUTES_BADGES_TYPES.COUNTER,
      value: toCountText(clanStarsNewRewards),
      id: ROUTES_BADGES.CLAN_STARS,
    };
  }

  // RECOMMENDATIONS_NEW: "NEW" within release window and not seen
  if (badge === ROUTES_BADGES.RECOMMENDATIONS_NEW) {
    const show = moment(settings.releaseFeaturesDates.clanrec2).add(2, 'weeks').isAfter(moment());
    if (!show || !currentAccount.id) {
      return {
        kind: ROUTES_BADGES_TYPES.NONE,
      };
    }

    const sawClanRec2 = getValueFromLocalStorage<Record<string, boolean>>('sawClanRec2');
    if (!sawClanRec2[currentAccount.id]) {
      return {
        kind: ROUTES_BADGES_TYPES.NEW,
        label: newLabel(),
        id: ROUTES_BADGES.RECOMMENDATIONS_NEW,
      };
    }

    return {
      kind: ROUTES_BADGES_TYPES.NONE,
    };
  }

  // TASKS: "NEW" within release window and not seen
  if (badge === ROUTES_BADGES.TASKS) {
    const show = moment(settings.releaseFeaturesDates.darwin).add(4, 'weeks').isAfter(moment());
    if (show && currentAccount.id) {
      const sawClanTasks = getValueFromLocalStorage<Record<string, boolean>>('sawClanTasks');
      if (!sawClanTasks[currentAccount.id]) {
        return {
          kind: ROUTES_BADGES_TYPES.NEW,
          label: newLabel(),
          id: ROUTES_BADGES.TASKS,
        };
      }
    }

    // TASKS: particle icon if finished tasks + permission
    if (clanTasksUnclaimedAmount > 0 && canClaimRewards) {
      return {
        kind: ROUTES_BADGES_TYPES.REWARD,
        id: ROUTES_BADGES.TASKS,
      };
    }

    return {
      kind: ROUTES_BADGES_TYPES.NONE,
    };
  }

  // CLAN_STARS: "NEW" if not seen
  if (badge === ROUTES_BADGES.CLAN_STARS) {
    if (currentAccount.id && !sawClanStars[currentAccount.id]) {
      return {
        kind: ROUTES_BADGES_TYPES.NEW,
        label: newLabel(),
        id: ROUTES_BADGES.CLAN_STARS,
      };
    }

    return {
      kind: ROUTES_BADGES_TYPES.NONE,
    };
  }

  // PATROLS: "NEW" if not seen
  if (badge === ROUTES_BADGES.PATROLS) {
    if (currentAccount.id && !sawPatrols[currentAccount.id]) {
      return {
        kind: ROUTES_BADGES_TYPES.NEW,
        label: newLabel(),
        id: ROUTES_BADGES.PATROLS,
      };
    }

    return {
      kind: ROUTES_BADGES_TYPES.NONE,
    };
  }

  return {
    kind: ROUTES_BADGES_TYPES.NONE,
  };
};
